import { useState, useRef, useCallback, useEffect } from 'react';
import {
  getRecordingMimeType,
  getPermissionStatus,
  MIN_RECORDING_DURATION_MS,
  MAX_RECORDING_DURATION_MS,
  RECORDING_WARMUP_MS,
} from '@/lib/audio/recorder';

type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'stopping';

interface UseAudioRecorderReturn {
  status: RecorderStatus;
  hasPermission: boolean | null;
  durationMs: number;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  cancel: () => void;
  clearError: () => void;
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<number>(0);
  const warmupTimerRef = useRef<number>(0);
  const resolveRef = useRef<((blob: Blob | null) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (warmupTimerRef.current) clearTimeout(warmupTimerRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const granted = await getPermissionStatus();
      if (cancelled || granted !== true) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setHasPermission(true);
      } catch {
        // Warm-up failed silently; startRecording will retry on demand.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setStatus('requesting');
    try {
      const cachedStream = streamRef.current;
      const isFreshStream =
        !cachedStream || cachedStream.getAudioTracks()[0]?.readyState !== 'live';
      const stream = isFreshStream
        ? await navigator.mediaDevices.getUserMedia({ audio: true })
        : cachedStream;
      streamRef.current = stream;
      setHasPermission(true);

      const mimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = 0;
        }
        if (warmupTimerRef.current) {
          clearTimeout(warmupTimerRef.current);
          warmupTimerRef.current = 0;
        }
        const elapsed = Date.now() - startTimeRef.current;

        if (elapsed < MIN_RECORDING_DURATION_MS) {
          setError('recording_too_short');
          setStatus('idle');
          if (resolveRef.current) resolveRef.current(null);
          return;
        }

        const blob = new Blob(chunksRef.current, { type: mimeType });
        setStatus('idle');
        if (resolveRef.current) resolveRef.current(blob);
      };

      recorder.onstart = () => {
        startTimeRef.current = Date.now();
        setDurationMs(0);
        timerRef.current = window.setInterval(() => {
          const elapsed = Date.now() - startTimeRef.current;
          setDurationMs(elapsed);
          if (elapsed >= MAX_RECORDING_DURATION_MS) {
            recorder.stop();
          }
        }, 100);

        if (isFreshStream) {
          // Let Safari's AGC/noise-suppression calibration settle before
          // signaling the user to speak; capture is already running underneath.
          warmupTimerRef.current = window.setTimeout(() => {
            setStatus('recording');
          }, RECORDING_WARMUP_MS);
        } else {
          setStatus('recording');
        }
      };

      recorder.start(100);
    } catch {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setHasPermission(false);
      setError('permission_denied');
      setStatus('idle');
    }
  }, []);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (
        !mediaRecorderRef.current ||
        mediaRecorderRef.current.state !== 'recording'
      ) {
        resolve(null);
        return;
      }
      setStatus('stopping');
      resolveRef.current = resolve;
      mediaRecorderRef.current.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      resolveRef.current = () => {};
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = 0;
    }
    if (warmupTimerRef.current) {
      clearTimeout(warmupTimerRef.current);
      warmupTimerRef.current = 0;
    }
    setStatus('idle');
    setDurationMs(0);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    status,
    hasPermission,
    durationMs,
    error,
    startRecording,
    stopRecording,
    cancel,
    clearError,
  };
}
